// Native shell polish (status bar, splash screen, Android hardware back).
//
// Everything here is a no-op on the web / installed PWA — each entry point is
// guarded by `isNativeApp()` and every plugin is loaded with a dynamic import
// so the web bundle never evaluates native plugin code at startup.

import { isNativeApp, isIOSNative, isAndroidNative } from "./platform";

/** HIBANE Desk sidebar / header brand blue — used for the Android status bar. */
export const NATIVE_STATUS_BAR_COLOR = "#0A3D7C";

let shellInitialised = false;

/**
 * Configure the native status bar:
 *  - iOS: light content over the brand-blue safe area (no overlay so our
 *    `env(safe-area-inset-top)` padding stays authoritative).
 *  - Android: solid brand-blue bar with light icons.
 */
async function initStatusBar(): Promise<void> {
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark }); // Dark style = light text
    if (isAndroidNative()) {
      await StatusBar.setBackgroundColor({ color: NATIVE_STATUS_BAR_COLOR });
      await StatusBar.setOverlaysWebView({ overlay: false });
    }
    if (isIOSNative()) {
      await StatusBar.setOverlaysWebView({ overlay: false });
    }
  } catch (err) {
    console.warn("[native] status bar unavailable", err);
  }
}

/** Hide the launch splash once React has painted. */
async function hideSplash(): Promise<void> {
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch (err) {
    console.warn("[native] splash screen unavailable", err);
  }
}

/**
 * Android hardware back button: navigate back inside the SPA when there is
 * history, otherwise minimise the app instead of killing it (killing the app on
 * back is the single most common complaint about wrapped web apps).
 */
async function initBackButton(): Promise<() => void> {
  try {
    const { App } = await import("@capacitor/app");
    const handle = await App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack && window.history.length > 1) {
        window.history.back();
      } else {
        void App.minimizeApp();
      }
    });
    return () => {
      void handle.remove();
    };
  } catch (err) {
    console.warn("[native] back button listener unavailable", err);
    return () => {};
  }
}

/**
 * Initialise the native shell. Safe to call on every platform; returns a
 * cleanup function for the listeners it registered.
 */
export async function initNativeShell(): Promise<() => void> {
  if (!isNativeApp() || shellInitialised) return () => {};
  shellInitialised = true;

  await initStatusBar();
  const removeBackButton = await initBackButton();
  // Give the first React paint a beat before dropping the splash.
  window.setTimeout(() => void hideSplash(), 350);

  return () => {
    removeBackButton();
    shellInitialised = false;
  };
}
